import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BaseDateService } from '../../../shared/services/base-date.service';
import { CustomerInquiryListItemResponse, CustomerInquiryDateResponse } from '../../../../api';
import {
  ShiftPlanCalendarDateInterface,
  SimilarInquiryMappedInterface,
  SimilarInquiriesDateInterface,
  ShiftPlanListBubbleItemInterface,
  BaseInquiryShiftBooleanInterface,
  ShiftPlanCalendarRangeInterface,
  SimilarInquiriesPayloadInterface,
} from '../_interfaces/base-shift-plan.interface';
import { BaseInquiryShiftsWithFlex, BaseInquiryShiftsUpperCase } from '../_enums/base-inquiry.enum';
import { EditInquiryFormInterface } from '../_interfaces/base-inquiry.interface';
import { FormRawValue } from '../_interfaces/base-form.interface';
import { baseInquiryActions, BaseCoreState } from '../../../_store';

@Injectable({ providedIn: 'root' })
export class BaseSimilarInquiriesService {
  constructor(private store: Store<BaseCoreState>) {}

  generateSimilarInquiries(
    inquiryList: CustomerInquiryListItemResponse[],
    calendar: ShiftPlanCalendarDateInterface[]
  ): SimilarInquiryMappedInterface[] {
    const result: SimilarInquiryMappedInterface[] = [];

    for (const inquiry of inquiryList) {
      const dateMap = new Map<string, CustomerInquiryDateResponse>();
      (inquiry.dates || []).forEach((dateItem) => {
        if (dateItem.date) {
          dateMap.set(dateItem.date, dateItem);
        }
      });

      const dates: SimilarInquiriesDateInterface[] = [];
      let availableShifts: BaseInquiryShiftBooleanInterface = {
        flex: false,
        fd: false,
        zd: false,
        sd: false,
        nd: false,
      };

      for (const calendarDate of calendar) {
        const dateString = calendarDate.dateItem.dateString;
        const date = calendarDate.dateItem.date ?? BaseDateService.toDate(dateString);
        const currentInquiryDate = dateMap.get(dateString);

        dates.push({
          date: dateString,
          isToday: BaseDateService.isToday(date),
          isWeekend: BaseDateService.isWeekendDay(date),
          bubbles: this.generateBubbles(currentInquiryDate),
        });

        if (currentInquiryDate) {
          availableShifts = this.defineVisibleShifts(currentInquiryDate, availableShifts);
        }
      }

      result.push({
        data: inquiry,
        dates,
        availableShifts,
      });
    }

    return result;
  }

  private generateBubbles(inquiryDate: CustomerInquiryDateResponse | undefined): ShiftPlanListBubbleItemInterface[] {
    const bubbles: ShiftPlanListBubbleItemInterface[] = [];

    Object.values(BaseInquiryShiftsWithFlex).forEach((type) => {
      let bubble: ShiftPlanListBubbleItemInterface;

      if (inquiryDate) {
        if (type === BaseInquiryShiftsWithFlex.flex) {
          if (inquiryDate.flexShift) {
            bubble = {
              shift: type,
              showDot: false,
              value: (inquiryDate.shiftFd ||
                inquiryDate.shiftSd ||
                inquiryDate.shiftNd ||
                inquiryDate.shiftZd) as number,
            };
          } else {
            bubble = {
              shift: type,
              showDot: false,
              value: 0,
            };
          }
        } else {
          if (inquiryDate.flexShift) {
            bubble = {
              shift: type,
              showDot: (inquiryDate[BaseInquiryShiftsUpperCase[type]] as number) > 0,
              value: 0,
            };
          } else {
            bubble = {
              shift: type,
              showDot: false,
              value: inquiryDate[BaseInquiryShiftsUpperCase[type]] || 0,
            };
          }
        }
      } else {
        bubble = {
          shift: type,
          showDot: false,
          value: 0,
        };
      }

      if (bubble) {
        bubbles.push(bubble);
      }
    });

    return bubbles;
  }

  defineVisibleShifts(
    inquiryDate: CustomerInquiryDateResponse,
    visibleShifts: BaseInquiryShiftBooleanInterface
  ): BaseInquiryShiftBooleanInterface {
    return {
      flex: !visibleShifts.flex && inquiryDate?.flexShift ? inquiryDate?.flexShift : visibleShifts.flex,
      fd: !visibleShifts.fd && inquiryDate?.shiftFd ? inquiryDate?.shiftFd > 0 : visibleShifts.fd,
      zd: !visibleShifts.zd && inquiryDate?.shiftZd ? inquiryDate?.shiftZd > 0 : visibleShifts.zd,
      sd: !visibleShifts.sd && inquiryDate?.shiftSd ? inquiryDate?.shiftSd > 0 : visibleShifts.sd,
      nd: !visibleShifts.nd && inquiryDate?.shiftNd ? inquiryDate?.shiftNd > 0 : visibleShifts.nd,
    };
  }

  getSimilarInquiriesPayload(
    formValue: FormRawValue<FormGroup<EditInquiryFormInterface>>,
    ranges: ShiftPlanCalendarRangeInterface[]
  ): SimilarInquiriesPayloadInterface {
    return {
      department: formValue.customerDepartment?.id as number,
      startDate: ranges[0].rangeData.startDate,
      endDate: ranges[ranges.length - 1].rangeData.endDate,
      qualifications: formValue.qualifications.length ? formValue.qualifications?.join(',') : undefined,
      mainQualification: formValue.mainQualification?.id,
      shifts: Array.from(
        new Set(
          ranges.flatMap((r) =>
            Object.entries(r.rangeData.shifts)
              .filter(([_, value]) => value === 1)
              .map(([key]) => key)
          )
        )
      ).join(','),
    };
  }

  loadSimilarInquiries(
    formValue: FormRawValue<FormGroup<EditInquiryFormInterface>>,
    ranges: ShiftPlanCalendarRangeInterface[]
  ) {
    if (!formValue.customerDepartment?.id) {
      return;
    }

    if (ranges.length === 0) {
      return;
    }

    this.store.dispatch(
      baseInquiryActions.loadSimilarInquiries({
        payload: this.getSimilarInquiriesPayload(formValue, ranges),
      })
    );
  }
}
